import React from 'react';
import SearchBar from '../shared/SearchBar';
import LoadingSpinner from '../shared/LoadingSpinner';
import EmptyState from '../shared/EmptyState';

/**
 * RelationshipSplitPane - Main split pane component for managing user/organization relationships
 * 
 * @param {Object} props - Component props
 * @param {string} props.relViewMode - Current view mode ('user' or 'organization')
 * @param {Function} props.setRelViewMode - Called when view mode changes
 * @param {string} props.leftSearch - Search term for left pane
 * @param {Function} props.setLeftSearch - Called when left pane search changes
 * @param {Array} props.users - Array of user objects
 * @param {Array} props.organizations - Array of organization objects
 * @param {Array} props.filteredLeftUsers - Filtered users for left pane
 * @param {Array} props.filteredLeftOrganizations - Filtered organizations for left pane
 * @param {Array} props.displayedLeftUsers - Users to display in left pane
 * @param {Array} props.displayedLeftOrganizations - Organizations to display in left pane
 * @param {string|null} props.selectedId - Currently selected entity ID
 * @param {Function} props.setSelectedId - Called when selection changes
 * @param {string} props.searchAssign - Search term for assignment
 * @param {Function} props.setSearchAssign - Called when assignment search changes
 * @param {boolean} props.isAssignDropdownOpen - Whether assignment dropdown is open
 * @param {Function} props.setIsAssignDropdownOpen - Called when assignment dropdown toggle
 * @param {Function} props.handleAddMapping - Called when adding a mapping
 * @param {Array} props.filteredAssignOptions - Filtered options for assignment
 * @param {string|null} props.processingCell - Currently processing cell ID
 * @param {Function} props.handleRemoveMapping - Called when removing a mapping
 * @param {string|Object|null} props.selectedEntityName - Name of selected entity
 * @param {string} props.selectedIdForDetails - Selected ID for details display
 * @param {Array} props.mappedItems - Currently mapped items
 * @returns {React.ReactElement} Relationship split pane component
 */
const RelationshipSplitPane = ({
  relViewMode,
  setRelViewMode,
  leftSearch,
  setLeftSearch,
  users,
  organizations,
  filteredLeftUsers,
  filteredLeftOrganizations,
  displayedLeftUsers,
  displayedLeftOrganizations,
  selectedId,
  setSelectedId,
  searchAssign,
  setSearchAssign,
  isAssignDropdownOpen,
  setIsAssignDropdownOpen,
  handleAddMapping,
  filteredAssignOptions,
  processingCell,
  handleRemoveMapping,
  selectedEntityName,
  selectedIdForDetails,
  mappedItems
}) => {
  return (
    <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg-card)', height: '650px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      {/* Left List Pane */}
      <div style={{ width: '350px', minWidth: '350px', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', background: 'var(--bg-secondary)', height: '100%' }}>
        <div style={{ padding: '1rem', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '0.75rem', background: 'var(--bg-card)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-primary)' }}>Manage Relationships By</span>
            <select 
              className="input-field"
              value={relViewMode}
              onChange={e => {
                setRelViewMode(e.target.value);
                setSelectedId(null);
              }}
              style={{ width: 'auto', padding: '0.3rem 1.5rem 0.3rem 0.5rem', fontSize: '0.8rem', margin: 0 }}
            >
              <option value="user">Users</option>
              <option value="organization">Organizations</option>
            </select>
          </div>
          <SearchBar
            value={leftSearch}
            onChange={e => setLeftSearch(e.target.value)}
            placeholder={relViewMode === 'user' ? "Search users..." : "Search organizations..."}
            iconSize="0.8rem"
            iconLeft="0.75rem"
            inputPaddingLeft="2rem"
            inputStyle={{ fontSize: '0.85rem', height: '32px' }}
          />
        </div>

        {/* List container */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {relViewMode === 'user' ? (
            displayedLeftUsers.length === 0 ? (
              <EmptyState message="No users found." variant="center" />
            ) : (
              <>
                {displayedLeftUsers.map(u => {
                  const isSelected = selectedId === u.id;
                  return (
                    <div
                      key={u.id}
                      onClick={() => {
                        setSelectedId(u.id);
                        setSearchAssign('');
                        setIsAssignDropdownOpen(false);
                      }}
                      style={{
                        padding: '0.75rem 1rem',
                        background: isSelected ? 'var(--bg-primary)' : 'transparent',
                        border: '1px solid',
                        borderColor: isSelected ? 'var(--border)' : 'transparent',
                        borderRadius: 'calc(var(--radius) - 2px)',
                        cursor: 'pointer',
                        transition: 'var(--transition)'
                      }}
                      className="table-row-hover"
                    >
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {u.fullName || u.email}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'monospace' }}>
                        {u.email}
                      </div>
                    </div>
                  );
                })}
                {filteredLeftUsers.length > 100 && (
                  <div style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)', borderTop: '1px solid var(--border)', marginTop: '0.5rem', background: 'var(--bg-card)', borderRadius: 'var(--radius)' }}>
                    Showing top 100 of {filteredLeftUsers.length} users. Search to narrow results.
                  </div>
                )}
              </>
            )
          ) : (
            filteredLeftOrganizations.length === 0 ? (
              <EmptyState message="No organizations found." variant="center" />
            ) : (
              <>
                {displayedLeftOrganizations.map(t => {
                  const isSelected = selectedId === t.id;
                  return (
                    <div
                      key={t.id}
                      onClick={() => {
                        setSelectedId(t.id);
                        setSearchAssign('');
                        setIsAssignDropdownOpen(false);
                      }}
                      style={{
                        padding: '0.75rem 1rem',
                        background: isSelected ? 'var(--bg-primary)' : 'transparent',
                        border: '1px solid',
                        borderColor: isSelected ? 'var(--border)' : 'transparent',
                        borderRadius: 'calc(var(--radius) - 2px)',
                        cursor: 'pointer',
                        transition: 'var(--transition)'
                      }}
                      className="table-row-hover"
                    >
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {t.name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'monospace' }}>
                        {t.id}
                      </div>
                    </div>
                  );
                })}
                {filteredLeftOrganizations.length > 100 && (
                  <div style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)', borderTop: '1px solid var(--border)', marginTop: '0.5rem', background: 'var(--bg-card)', borderRadius: 'var(--radius)' }}>
                    Showing top 100 of {filteredLeftOrganizations.length} organizations. Search to narrow results.
                  </div>
                )}
              </>
            )
          )}
        </div>
      </div>

      {/* Right Details & Action Pane */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', height: '100%', overflowY: 'auto', padding: '1rem 1.25rem' }}>
        {selectedId ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Header Info */}
            <div>
              <h2 style={{ fontSize: '1.5rem', margin: 0, fontWeight: 700, color: 'var(--text-primary)' }}>
                {selectedEntityName}
              </h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0.25rem 0 0 0', fontFamily: 'monospace' }}>
                ID: {selectedIdForDetails}
              </p>
            </div>

            {/* Autocomplete Assignment Control */}
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '1.5rem', position: 'relative', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <h3 style={{ fontSize: '0.95rem', margin: 0, fontWeight: 600, color: 'var(--text-primary)' }}>
                {relViewMode === 'user' ? 'Assign to Organization' : 'Add Member User'}
              </h3>
              
              <div style={{ position: 'relative' }}>
                <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }}></i>
                <input 
                  className="input-field"
                  value={searchAssign}
                  onChange={e => {
                    setSearchAssign(e.target.value);
                    setIsAssignDropdownOpen(true);
                  }}
                  onFocus={() => setIsAssignDropdownOpen(true)}
                  placeholder={relViewMode === 'user' ? "Type to search and select organization..." : "Type to search and select user..."}
                  style={{ paddingLeft: '2.5rem', margin: 0 }}
                />

                {/* Dropdown Results */}
                {isAssignDropdownOpen && (
                  <>
                    <div 
                      onClick={() => setIsAssignDropdownOpen(false)} 
                      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99, background: 'transparent' }} 
                    />
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                      zIndex: 100,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      padding: '4px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px'
                    }}>
                      {filteredAssignOptions.length === 0 ? (
                        <EmptyState message="No matches found." variant="inline" />
                      ) : (
                        filteredAssignOptions.map(opt => (
                          <button
                            key={opt.id}
                            onClick={() => handleAddMapping(opt.id)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              borderRadius: 'calc(var(--radius) - 4px)',
                              color: 'var(--text-primary)',
                              cursor: 'pointer',
                              padding: '0.5rem 0.75rem',
                              display: 'flex',
                              flexDirection: 'column',
                              textAlign: 'left',
                              width: '100%',
                              transition: 'var(--transition)'
                            }}
                            className="table-row-hover"
                          >
                            <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{opt.name}</span>
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{opt.id}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Current Mappings List */}
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
              <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)', fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                {relViewMode === 'user' ? 'Assigned Organizations' : 'Member Users'} ({mappedItems.length})
              </div>
              {mappedItems.length === 0 ? (
                <EmptyState message="No mappings active." variant="inline" />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {mappedItems.map((item, index) => {
                    const cellKey = relViewMode === 'user' ? `${selectedIdForDetails}-${item.id}` : `${item.id}-${selectedIdForDetails}`;
                    const isProcessing = processingCell === cellKey;
                    return (
                      <div 
                        key={item.id} 
                        style={{
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          padding: '0.75rem 1.25rem', 
                          borderBottom: index < mappedItems.length - 1 ? '1px solid var(--border)' : 'none' 
                        }}
                        className="table-row-hover"
                      >
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)' }}>{item.name}</div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>ID: {item.id}</div>
                        </div>
                        <button
                          disabled={isProcessing}
                          onClick={() => handleRemoveMapping(item.id)}
                          className="family-menu-trigger delete"
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--error)', padding: '4px 8px', borderRadius: '4px' }}
                          title={relViewMode === 'user' ? 'Remove Organization Mapping' : 'Remove User Mapping'}
                        >
                          {isProcessing ? (
                            <LoadingSpinner size="small" style={{ margin: 0 }} />
                          ) : (
                            <i className="fa-solid fa-unlink"></i>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', color: 'var(--text-secondary)', textAlign: 'center', gap: '1rem' }}>
            <i className="fa-solid fa-network-wired" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>No Selection</h3>
              <p style={{ fontSize: '0.85rem', margin: 0 }}>
                Select a {relViewMode === 'user' ? 'user' : 'organization'} from the left pane to manage assignments.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RelationshipSplitPane;